import React, { useState, KeyboardEvent, useRef, Fragment, useEffect } from 'react'
import {
  Box,
  Text,
  Stack,
  Spread,
  Inline,
  TextLinkButton,
  Divider,
  Badge,
  IconGift,
  useToast,
  ButtonIcon
} from '@machobear/design-system'
import { useTranslation } from 'react-i18next'
import {
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths
} from 'date-fns'
import { Userpilot } from 'userpilot'
import useClickOutside from '@/hooks/use-click-outside'
import { useGetPromotionNotifications, useMarkPromotionNotification } from '@/hooks/use-promotions'
import { type Promotion } from '@/hooks/use-promotions'
import useLocalStorage from '@/hooks/use-localstorage'
import { pushToDatalayer } from '@/utils/push-datalayer'

import config from '@/config'
import Ellipse from './ellipse.svg'
import EllipseOnIcon from './ellipseOnIcon.svg'
import { listCardStyle, listWrapperStyle, redEllipseStyle, titleWrapperStyle } from './styles.css'

const { APP_PATH, WEB_URL } = config

type RedEllipseProps = {
  src: string
  className?: string
  position?: 'absolute' | 'relative'
}

interface PromotionCardProp extends Promotion {
  lastItem: boolean
  isActive: boolean
}

type TimeUnit = {
  diff: number
  limit: number
  key: string
  format: string
}

const tagMap = {
  free_promotion: { name: 'Free', tone: 'positive' as const },
  optional_promotion: { name: 'Optional', tone: 'promote' as const },
  limited_time_offer: { name: 'Limited Time Offer', tone: 'positive' as const }
}

const promotions = {
  '1 Tenant Screening credit': {
    trackingName: 'freescreenrpt',
    link: `${APP_PATH}/usage-credits/products/my-products`
  },
  '7-day Sponsored Listings': {
    trackingName: 'free7daysl',
    link: `${APP_PATH}/usage-credits/products/my-products`
  },
  'Black Friday Sale': {
    trackingName: 'blackfriday',
    link: `${WEB_URL}/landlords/promotions`
  },
  'Our annual holiday deals are here!': {
    trackingName: 'blackfriday',
    link: `${WEB_URL}/landlords/promotions`
  }
}

const PromotionList = () => {
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const { t } = useTranslation()

  const { data: promotionList } = useGetPromotionNotifications()
  const { markAllPromotions } = useMarkPromotionNotification()

  const showToast = useToast()

  const closeList = () => {
    setOpen(false)
    setActiveIndex(null)
  }

  const ref = useClickOutside(closeList)
  const listRef = useRef<HTMLUListElement>(null)
  const iconButtonRef = useRef<HTMLButtonElement>(null)

  const label = t('promotions.title', 'Promotions')
  const haveUnread = promotionList?.some(promotion => promotion.read_state === 'not_read')

  const handleIconClick = () => {
    const trackingEventId = 'lakshimi-click-promo-icon'
    setOpen(!open)

    if (!open) {
      Userpilot.track(trackingEventId)
      pushToDatalayer({
        event: trackingEventId
      })
    }
  }

  const handleMarkAllAsRead = async () => {
    const trackingEventId = 'lakshimi-click-promo-anchor-markallasread'
    Userpilot.track(trackingEventId)
    pushToDatalayer({
      event: trackingEventId
    })
    await markAllPromotions.mutateAsync({
      onError: error => showToast({ message: error, tone: 'critical' })
    })
  }

  const handleButtonKeyboard = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleIconClick()
    }

    if ((e.key === 'Tab' && !promotionList?.length) || e.key === 'Escape') {
      setOpen(false)
    }
  }

  const handleListKeyboard = (e: KeyboardEvent) => {
    if (!promotionList?.length) return

    if (e.key === 'ArrowDown') {
      setActiveIndex(prev => {
        const next = prev === null ? 0 : prev < promotionList.length - 1 ? prev + 1 : prev
        return next
      })
      return
    }

    if (e.key === 'ArrowUp') {
      setActiveIndex(prev => {
        if (prev === null) return null
        const next = prev > 0 ? prev - 1 : prev
        return next
      })
      return
    }

    if (e.shiftKey && e.key === 'Tab') {
      return
    }

    if (e.key === 'Tab') {
      setOpen(false)
    }

    if (e.key === 'Escape') {
      setOpen(false)
      iconButtonRef.current?.focus()
    }
  }

  return (
    <Box position="relative" ref={ref}>
      <Box position="relative">
        <ButtonIcon
          id={label}
          type="button"
          label={label}
          aria-expanded={open}
          aria-controls="promotions-list"
          aria-haspopup="listbox"
          tabIndex={0}
          onClick={handleIconClick}
          onKeyDown={handleButtonKeyboard}
          icon={<IconGift />}
          variant="transparent"
          size="large"
          ref={iconButtonRef}
        />

        {haveUnread && (
          <RedEllipse className={redEllipseStyle} position="absolute" src={EllipseOnIcon} />
        )}
      </Box>

      {open && (
        <Box
          position="absolute"
          className={listWrapperStyle}
          border="borderNeutralLight"
          borderRadius="large"
          role="listbox"
          as="ul"
          ref={listRef}
          id="promotions-list"
          aria-label={label}
          onKeyDown={handleListKeyboard}
          zIndex="dropdown"
          background="white"
        >
          <Box
            paddingY="gutter"
            background="white"
            paddingX="xsmall"
            borderTopRightRadius="large"
            borderTopLeftRadius="large"
          >
            <Spread space="gutter" alignY={'center'}>
              <Inline space="xxsmall" alignY="center">
                <IconGift size="large" />
                <Text weight="strong">{label}</Text>
              </Inline>
              {!!promotionList?.length && (
                <Text size="xsmall" tone="link">
                  <TextLinkButton
                    id="mark-all-as-read"
                    weight="regular"
                    onClick={handleMarkAllAsRead}
                    tabIndex={0}
                  >
                    {t('promtions.mark_all_as_read.text', 'Mark all as read')}
                  </TextLinkButton>
                </Text>
              )}
            </Spread>
          </Box>
          <Divider />

          {promotionList?.length === 0 ? (
            <Box
              paddingY="xxlarge"
              paddingX="xsmall"
              background="white"
              borderBottomRightRadius="large"
              borderBottomLeftRadius="large"
            >
              <Stack space="small" align="center">
                <Text weight="medium">{t('promtions.empty.title', 'Stay tuned!')}</Text>
                <Text tone="secondary" size="small" align="center">
                  {t(
                    'promtions.empty.description',
                    'Check back regularly for the latest promotions.'
                  )}
                </Text>
              </Stack>
            </Box>
          ) : (
            <Box role="ui" aria-labelledby="mark-all-as-read">
              {promotionList?.map((item: Promotion, i) => {
                const lastItem = i === promotionList.length - 1
                return (
                  <Fragment key={item.promotion_id}>
                    <ListCard {...item} lastItem={lastItem} isActive={i === activeIndex} />
                    {!lastItem && <Divider />}
                  </Fragment>
                )
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default PromotionList

const ListCard = ({
  promotion_id,
  title,
  description,
  lastItem,
  tag,
  read_state,
  applied_datetime,
  isActive,
  start_datetime
}: PromotionCardProp) => {
  const { t } = useTranslation()
  const { markSinglePromotion } = useMarkPromotionNotification()
  const borderRadius = lastItem ? 'large' : 'none'
  const isRead = read_state === 'read'
  const showToast = useToast()
  const [, setPromoType] = useLocalStorage('promoType')

  const listCardRef = useRef<HTMLLIElement>(null)

  const isBFPromotion = tag.toLocaleLowerCase() === 'limited_time_offer'

  const handleClick = async () => {
    const currentPromotion = promotions?.[title as keyof typeof promotions] ?? {}
    const promotionName = currentPromotion?.trackingName
    const linkPath = currentPromotion?.link
    setPromoType(promotionName)
    Userpilot.track(`lakshimi-click-promo-${promotionName}`)
    pushToDatalayer({
      event: `lakshimi-click-promo-${promotionName}`,
      promoType: promotionName
    })

    !isRead
      ? await markSinglePromotion.mutateAsync({
          promotionId: promotion_id,
          onSuccess: () => window.open(linkPath),
          onError: error => showToast({ message: error, tone: 'critical' })
        })
      : window.open(linkPath)
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleClick()
    }
  }

  const now = new Date()
  const date = new Date(isBFPromotion ? start_datetime : applied_datetime)

  const timeUnits: TimeUnit[] = [
    {
      diff: differenceInMinutes(now, date),
      limit: 3,
      key: 'just_now_text',
      format: 'Just now'
    },
    {
      diff: differenceInMinutes(now, date),
      limit: 60,
      key: 'minutes_text',
      format: 'minutes'
    },
    {
      diff: differenceInHours(now, date),
      limit: 24,
      key: 'hours_text',
      format: 'hour'
    },
    {
      diff: differenceInDays(now, date),
      limit: 7,
      key: 'days_text',
      format: 'day'
    },
    {
      diff: differenceInWeeks(now, date),
      limit: 4,
      key: 'weeks_text',
      format: 'week'
    },
    {
      diff: differenceInMonths(now, date),
      limit: Infinity,
      key: 'months_text',
      format: 'month'
    }
  ]

  const formatNotificationTime = () => {
    const unit = timeUnits.find(unit => unit.diff < unit.limit)!
    if (unit.key === 'just_now_text') {
      return t(`promtions.${unit.key}`, unit.format)
    }

    return t(`promtions.${unit.key}`, `{{value}} ${unit.format}{{plural}} ago`, {
      value: unit.diff,
      plural: unit.diff > 1 ? 's' : ''
    })
  }

  const notificationTime = formatNotificationTime()
  const promotionTag = tagMap[tag.toLocaleLowerCase() as keyof typeof tagMap]

  useEffect(() => {
    if (isActive) {
      listCardRef.current?.focus()
    }
  }, [isActive])

  return (
    <Box
      className={listCardStyle[(isRead && isActive) || !isRead ? 'new' : 'default']}
      paddingX="xsmall"
      paddingY="small"
      cursor="pointer"
      borderBottomRightRadius={borderRadius}
      borderBottomLeftRadius={borderRadius}
      role="option"
      as="li"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      aria-selected={isActive}
      aria-live={isRead ? 'off' : 'polite'}
      ref={listCardRef}
    >
      <Stack space="medium">
        <Stack space="small">
          <Spread alignY="center" space="xsmall">
            <Inline space="xsmall" alignY="center">
              <Box className={titleWrapperStyle}>
                <Text weight={isRead ? 'regular' : 'strong'} tone="neutral" maxLines={1}>
                  {title}
                </Text>
              </Box>

              <Badge tone={promotionTag?.tone} weight="strong">
                {promotionTag?.name}
              </Badge>
            </Inline>

            {!isRead && <RedEllipse src={Ellipse} />}
          </Spread>
          <Box maxWidth="xsmall">
            <Text size="small" weight="medium" tone="secondary" maxLines={2}>
              {description}
            </Text>
          </Box>
        </Stack>
        <Text size="xsmall" tone="tertiary">
          {notificationTime}
        </Text>
      </Stack>
    </Box>
  )
}

const RedEllipse = (props: RedEllipseProps) => (
  <Box component="img" alt="new notification icon" {...props} zIndex={1} />
)
